import { all } from 'app/releases'
import { removeOpacity } from 'app/views/search/colorUtils.js'
import { SECONDARY_TONE_OPACITY } from 'app/store/modules/iconComposition'

const CURRENT_YEAR = new Date().getFullYear()
const DEFAULT_SECONDARY_OPACITY = 0.4
const DEFAULT_SVG_SIZE = 16
const ALL_VERSIONS = all.together

export const transformIconColoring = function (styling, defaultStyles, colorPrimary, colorSecondary, opacityPrimary, opacitySecondary) {
  const styleArray = []
  let stringStyle = ''

  if (
    styling.colorPrimary === defaultStyles.colorPrimary &&
    styling.colorSecondary === defaultStyles.colorSecondary &&
    styling.opacityPrimary === defaultStyles.opacityPrimary &&
    styling.opacitySecondary === defaultStyles.opacitySecondary
  ) {
    return ''
  }

  if (styling?.colorPrimary && styling?.opacityPrimary == null) {
    styleArray.push(colorPrimary)
  }

  if (styling?.colorSecondary && styling?.opacitySecondary == null) {
    styleArray.push(colorSecondary)
  }

  if (styling?.opacityPrimary != null) {
    styleArray.push(colorPrimary)
    styleArray.push(opacityPrimary)
  }

  if (styling?.opacitySecondary != null) {
    styleArray.push(colorSecondary)
    styleArray.push(opacitySecondary)
  }

  styleArray.forEach((element) => {
    stringStyle += element
  })

  return stringStyle
}

export const handleSize = function (size) {
  switch (size) {
    case '2xs':
      return 10
    case 'xs':
      return 12
    case 'sm':
      return 14
    case 'lg':
      return 20
    case 'xl':
      return 24
    case '2xl':
      return 32
    default:
      return DEFAULT_SVG_SIZE
  }
}

export const svgStylingDifference = function (styling, defaultStyles) {
  let stylingChanges = {}

  // if duotone svg, and colorSecondary is equal to default, and colorPrimary has changed, then colorSecondary should be set to colorPrimary
  if (
    (styling?.colorSecondary === defaultStyles?.colorSecondary && styling?.colorPrimary !== defaultStyles?.colorPrimary) ||
    (styling?.colorPrimary !== defaultStyles?.colorPrimary && styling?.colorSecondary === defaultStyles?.colorSecondary && !stylingChanges?.colorSecondary)
  ) {
    stylingChanges = {
      colorPrimary: defaultStyles.colorPrimary === styling.colorPrimary ? null : styling.colorPrimary,
      colorSecondary: styling.colorPrimary,
      opacityPrimary: defaultStyles.opacityPrimary === styling.opacityPrimary ? null : styling.opacityPrimary,
      opacitySecondary: defaultStyles.opacitySecondary === styling.opacitySecondary ? null : styling.opacitySecondary,
      size: handleSize(styling.size)
    }
  } else {
    stylingChanges = {
      colorPrimary: defaultStyles?.colorPrimary === styling?.colorPrimary ? null : styling.colorPrimary,
      colorSecondary: defaultStyles?.colorSecondary === styling?.colorSecondary ? null : styling.colorSecondary,
      opacityPrimary: defaultStyles?.opacityPrimary === styling?.opacityPrimary ? null : styling.opacityPrimary,
      opacitySecondary: defaultStyles?.opacitySecondary === styling?.opacitySecondary ? null : styling.opacitySecondary,
      size: handleSize(styling.size)
    }
  }

  return stylingChanges
}

export const svgCodeSnippet = function ({ viggo, iconAbstract, svgStylingDifference, iconLicenseType, version }) {
  const { SvgIcon, SvgOutputOptions, SvgStylesheetMode } = viggo

  const iconHeightToWidthRatio = iconAbstract.height / iconAbstract.width

  const params = {
    roundToDecimalPlaces: 1,
    attributionCommentBody: `Font Awesome ${iconLicenseType} ${version} by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license${
      iconLicenseType === 'Free' || iconLicenseType === 'Brand' ? '/free' : ' (Commercial License)'
    } Copyright ${CURRENT_YEAR} Fonticons, Inc.`,
    pathFills: [removeOpacity(svgStylingDifference.colorPrimary), removeOpacity(svgStylingDifference.colorSecondary)]
  }

  if (svgStylingDifference.size !== DEFAULT_SVG_SIZE) {
    params.height = svgStylingDifference.size.toString()
    params.width = (svgStylingDifference.size / iconHeightToWidthRatio).toString()
  }

  if (svgStylingDifference.opacityPrimary || svgStylingDifference.opacitySecondary) {
    const opacitySecondary = (svgStylingDifference.opacitySecondary || DEFAULT_SECONDARY_OPACITY).toString()
    const opacityPrimary = svgStylingDifference.opacityPrimary ? svgStylingDifference.opacityPrimary.toString() : undefined
    params.duotoneOpacities = [opacityPrimary, opacitySecondary]
  }

  const outputOptions = new SvgOutputOptions(params)
  outputOptions.configAsPresentationAttributesFile()

  const svg = SvgIcon.fromParts(iconAbstract.width, iconAbstract.height, iconAbstract.primaryPath, iconAbstract.secondaryPath, iconAbstract.isDuotone)

  return svg.toStringWithOptions(outputOptions, SvgStylesheetMode.Append)
}

export const fetchSvg = async function ({ viggo, isCustomIcon, icon, activeVersion, styling, isFullSvg = false }) {
  if (isCustomIcon) {
    if (isFullSvg) {
      // Only supported for Font Awesome 7 and above
      const { SvgIcon, SvgSizingParams } = viggo
      const iconSVG = SvgIcon.fromParts(icon.width, icon.height, icon.path, icon.pathSecondary, !!icon.pathSecondary)

      iconSVG.formatForFullCanvas(SvgSizingParams.fa7Default())

      return {
        icon: icon,
        width: iconSVG?.width(),
        height: iconSVG?.height(),
        isDuotone: iconSVG?.isMultitone(),
        primaryPath: iconSVG?.primaryPath(),
        secondaryPath: iconSVG?.secondaryPath()
      }
    } else {
      // Handles cropped and older versions
      return {
        icon: icon,
        width: icon?.width,
        height: icon?.height,
        isDuotone: !!icon?.pathSecondary,
        primaryPath: icon?.path,
        secondaryPath: icon?.pathSecondary
      }
    }
  } else {
    try {
      const response = await fetch(getResourceUrl(activeVersion, icon, styling, isFullSvg))

      if (response.status >= 400) {
        throw new Error('Bad response from server')
      }

      const iconSVG = viggo.parseStandard(await response.text())

      if (!iconSVG) {
        throw new Error('Error parsing SVG')
      }

      return {
        icon: icon,
        width: iconSVG?.width(),
        height: iconSVG?.height(),
        isDuotone: iconSVG?.isMultitone(),
        primaryPath: iconSVG?.primaryPath(),
        secondaryPath: iconSVG?.secondaryPath()
      }
    } catch (error) {
      console.error(error)
      throw new Error('Failed fetching or parsing svg')
    }
  }
}

export const getResourceUrl = function (activeVersion, icon, styling, isFullSvg) {
  const versionFound = !!ALL_VERSIONS.find((obj) => obj.version === activeVersion)

  try {
    if (!versionFound || !icon?.name || typeof styling == 'undefined') {
      throw new Error('Error: getResourceUrl()')
    }

    const svgPath = isFullSvg ? 'svgs-full' : 'svgs'

    switch (styling?.family) {
      case 'classic':
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/v${activeVersion}/${svgPath}/${styling?.style}/${icon?.name}.svg`
      case 'brands':
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/v${activeVersion}/${svgPath}/brands/${icon?.name}.svg`
      case 'duotone':
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/v${activeVersion}/${svgPath}/${styling?.family}${
          styling?.style === 'solid' ? '' : `-${styling.style}`
        }/${icon?.name}.svg`
      default:
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/v${activeVersion}/${svgPath}/${styling?.family}-${styling?.style}/${icon?.name}.svg`
    }
  } catch (error) {
    console.error(error.message)
  }
}

export const downloadPNG = async function ({ viggo, styling, icon, activeVersion, isCustomIcon }) {
  if (!styling.colorPrimary || !styling.colorSecondary) {
    throw new Error('downloadPNG: color properties are required on styling')
  }

  const isFullSvg = true // PNGs always use full
  const svg = await fetchSvg({ viggo, isCustomIcon, icon, activeVersion, styling, isFullSvg })

  const { SvgIcon, SvgOutputOptions } = viggo
  const iconSVG = SvgIcon.fromParts(svg.width, svg.height, svg.primaryPath, svg.secondaryPath, svg.isDuotone)

  const options = new SvgOutputOptions()
  options.configForColorPng({
    primaryColor: removeOpacity(styling.colorPrimary),
    primaryOpacity: styling.opacityPrimary,
    secondaryColor: removeOpacity(styling.colorSecondary),
    secondaryOpacity: styling.opacitySecondary
  })

  const pngBytes = iconSVG.renderPng(options)

  createDownload(pngBytes, icon, styling, isCustomIcon, false, 'image/png', 'png')
}

export const downloadSVG = async function ({ viggo, styling, defaultStyles, icon, activeVersion, isCustomIcon, isFullSvg }) {
  const licenseType = icon.is_free ? 'Free' : 'Pro'
  const svg = await fetchSvg({ viggo, isCustomIcon, icon, activeVersion, styling, isFullSvg })
  const colors = svgStylingDifference(styling, defaultStyles)
  const svgFinalized = svgCodeSnippet({ viggo, iconAbstract: svg, svgStylingDifference: colors, iconLicenseType: licenseType, version: activeVersion })

  createDownload(svgFinalized, icon, styling, isCustomIcon, isFullSvg, 'image/svg+xml', 'svg')
}

// Build a standalone SVG document string from icon path data using DOM APIs so
// the browser escapes attribute values. Defense-in-depth for the download
// flows: the server-side path-data regex is the primary boundary, but this
// avoids relying on string interpolation to keep the output well-formed.
// `secondaryPath` is optional: without it this emits exactly the single-path
// markup it always has. With it, the secondary tone is drawn first and at
// duotone's usual opacity, so a two-tone icon reads as two tones even where
// both are the same colour.
export const buildIconSvgMarkup = function ({ path, secondaryPath = null, width, height, fill = 'currentColor', secondaryOpacity = SECONDARY_TONE_OPACITY, pxWidth = null, pxHeight = null }) {
  const NS = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(NS, 'svg')
  svg.setAttribute('xmlns', NS)
  if (pxWidth != null) svg.setAttribute('width', String(pxWidth))
  if (pxHeight != null) svg.setAttribute('height', String(pxHeight))
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
  if (secondaryPath) {
    const secondaryEl = document.createElementNS(NS, 'path')
    secondaryEl.setAttribute('d', secondaryPath)
    secondaryEl.setAttribute('fill', fill)
    secondaryEl.setAttribute('opacity', String(secondaryOpacity))
    svg.appendChild(secondaryEl)
  }
  const pathEl = document.createElementNS(NS, 'path')
  pathEl.setAttribute('d', path)
  pathEl.setAttribute('fill', fill)
  svg.appendChild(pathEl)
  return new XMLSerializer().serializeToString(svg)
}

export const createDownload = function (data, icon, styling, isCustomIcon, addFullToName, mimeType, fileExtension) {
  const blobUrl = URL.createObjectURL(new Blob([data], { type: mimeType }))
  const link = window.document.createElement('a')
  const name = [icon.name]

  if (!isCustomIcon) {
    if (styling.family === 'classic') {
      name.push(styling.style)
    } else {
      name.push(styling.family)
      name.push(styling.style)
    }
  }

  link.href = blobUrl
  link.download = `${name.join('-')}${addFullToName ? '-full' : ''}.${fileExtension}`
  link.click()

  URL.revokeObjectURL(blobUrl)
}
